import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine} from '@dropbox/dig-icons/assets';
import {MouseEvent, ReactNode} from 'react';

import styles from './SearchBox.module.css';

/**
 * /workstreams-style list: one outlined group, rows split by hairlines. Each
 * row is a lead (time, face, glyph), a title and an optional subtle meta line,
 * with a chevron on the far right that only shows on the row you're on
 * (hovered or selected).
 */
export const GroupList = ({children}: {children: ReactNode}) => (
  <div className={styles.group}>{children}</div>
);

/** One row; what a click does (show it in the pane, or go there) is the list's. */
export const GroupRow = ({
  lead,
  title,
  tag,
  meta,
  wideLead = false,
  onClick,
  onHover,
  selected = false,
  rowId,
}: {
  lead: ReactNode;
  title: string;
  /** A quiet word right after the title ("Manager", "Team"). */
  tag?: string;
  meta?: ReactNode;
  /** A lead that's text (a meeting's time) gets a wider column. */
  wideLead?: boolean;
  /** Gets the event, so a ⌘-click can open a new tab. */
  onClick: (event: MouseEvent) => void;
  /** The pointer arrived: a chance to start loading what the pane will show. */
  onHover?: () => void;
  /** Shown in the pane right now. */
  selected?: boolean;
  /** The row's id, so the arrow keys can scroll it into view. */
  rowId?: string;
}) => (
  <div
    data-row-id={rowId}
    {...withShade({className: `${styles.groupRow}${selected ? ` ${styles.surface}` : ''}`})}
    onClick={onClick}
    onMouseEnter={onHover}
  >
    <div className={`${styles.rowLead}${wideLead ? ` ${styles.rowLeadWide}` : ''}`}>{lead}</div>
    <div className={styles.rowBody}>
      <div className={styles.rowTitle}>
        <Text isBold tagName="div">
          <Truncate>{title}</Truncate>
        </Text>
        {tag && (
          <Text size="small" color="subtle" className={styles.rowTag}>
            {tag}
          </Text>
        )}
      </div>
      {meta}
    </div>
    <div className={`${styles.rowChevron}${selected ? ` ${styles.rowChevronShown}` : ''}`}>
      <UIIcon
        src={ChevronRightLine}
        size="small"
        className={atoms({color: 'Text Subtle'})}
        aria-hidden
      />
    </div>
  </div>
);
